/**
 * Name and value of the auth-state cache-key query parameter (PLT-5219).
 *
 * Internal API auth rides in the HttpOnly `up-access` cookie (PLT-4720), so
 * no HTTP cache can key entries on auth state: `Vary: Authorization` matches
 * everything (internal requests carry no Authorization header), browsers do
 * not reliably vary on cookies, and CloudFront ignores origin `Vary`
 * altogether. On `auth: { mode: 'optional' }` routes — which answer a
 * credential-less request as anonymous instead of 401ing — this allows a
 * publicly-cacheable *anonymous* 200 to be stored under the same cache key
 * as the logged-in response and later served to a logged-in viewer
 * (sporadic bogus paywalls, the PLT-5124 redirect↔login loop).
 *
 * Fix: the SPA appends `?auth=1` to internal GET requests whenever it
 * believes it is authenticated. That puts the believed auth state into the
 * URL — the one part of the request that is in the cache key of *every*
 * cache: the browser keys on URL, and CloudFront's `/api/*` cache policy
 * forwards all query strings. Anonymous and authenticated responses
 * therefore live under different keys everywhere, with no `Vary` and no
 * CloudFront policy change.
 *
 * The parameter is a *claim*, not a credential — and the poisoning scenario
 * is precisely a request whose claim is wrong (cookie silently expired, so
 * the request arrives genuinely anonymous). Two server-side rules in
 * `upserve` make the claimed key trustworthy:
 * - `registerAuthClaimExtraction` strips the parameter in an `onRequest`
 *   extension before routing, so no route handler or Joi query schema ever
 *   sees it (caches key on the *original* URL regardless);
 * - the cache mismatch guard in `upserve/index.ts` forces `ttl(0)` on any
 *   response where the request signalled an authenticated caller but the
 *   server's actual auth decision was anonymous. Any entry that exists
 *   under an `?auth=1` key is thus guaranteed to be a genuinely
 *   authenticated response (which, with `CACHING_FOR_LOGGED_IN_USERS =
 *   'none'`, is never cached at all).
 *
 * Lives in `common` because both sides need the name and the UI
 * intentionally does not cross-import from `@upright/main`:
 * - the SPA appends it in `ui/api/common/fetcher-core.ts`,
 * - the server strips and evaluates it in
 *   `upserve/common/session/auth-claim.ts`.
 */
export const AUTH_CLAIM_QUERY_PARAM = 'auth';
export const AUTH_CLAIM_QUERY_VALUE = '1';
